import { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { theme } from '@garnish/constants';

import { AllergenProperty } from '@sg/graphql-schema';

import type { Asset, Entry } from 'contentful';

/**
 * Returns the contentful data for home.
 */
export const useHomeContent = (content: HomeContent | undefined) => {
  const { formatMessage } = useIntl();

  return useMemo(() => {
    const filtersTitle = content?.filtersTitle ?? formatMessage(fallbacks.filtersTitle);
    const filterableCategories = getCategories(content?.filterableCategories ?? []);
    const genericCategories = getCategories(content?.genericCategories ?? []);

    const homeBannerSignedOutXs = (content?.homeBannerSignedOutXs?.map(mapBannerContent) ?? []).filter(({ image }) => Boolean(image)); // prettier-ignore
    const homeBannerSignedOutLg = (content?.homeBannerSignedOutLg?.map(mapBannerContent) ?? []).filter(({ image }) => Boolean(image)); // prettier-ignore
    const homeBannerSignedInXs = (content?.homeBannerSignedInXs?.map(mapBannerContent) ?? []).filter(({ image }) => Boolean(image)); // prettier-ignore
    const homeBannerSignedInLg = (content?.homeBannerSignedInLg?.map(mapBannerContent) ?? []).filter(({ image }) => Boolean(image)); // prettier-ignore

    const fallBackBanner: Required<HomeBannerContent> = {
      id: 'fallback-banner',
      image: '',
      imageA11y: formatMessage(fallbacks.fallbackImageA11y),
      title: formatMessage(fallbacks.fallbackBannerTitle, { br: renderBreak }),
      description: formatMessage(fallbacks.fallbackBannerDescription),
      cta: formatMessage(fallbacks.fallbackBannerCta),
      foregroundColor: theme.colors.WHITE,
      deepLink: '/menu',
    };

    const fallbackPostOrderBanner: Required<HomeBannerContent> = {
      id: 'fallback-post-order-banner',
      image: '',
      imageA11y: formatMessage(fallbacks.postOrderBannerImageA11y),
      title: formatMessage(fallbacks.postOrderBannerTitle),
      description: formatMessage(fallbacks.postOrderBannerDescription),
      cta: formatMessage(fallbacks.postOrderBannerCta),
      deepLink: evergreenLink,
      foregroundColor: theme.colors.WHITE,
    };

    const fallbackPostOrderCard: Required<HomeBannerContent> = {
      id: 'fallback-post-order-card',
      title: formatMessage(fallbacks.postOrderCardTitle),
      description: formatMessage(fallbacks.postOrderCardDescription),
      cta: formatMessage(fallbacks.postOrderCardCta),
      foregroundColor: theme.colors.WHITE,
      deepLink: '',
      image: '',
      imageA11y: '',
    };

    const fallbackPostOrderCardMultiple: Required<HomeBannerContent> = {
      id: 'fallback-post-order-card',
      title: formatMessage(fallbacks.postOrderCardMultipleTitle),
      description: formatMessage(fallbacks.postOrderCardDescription),
      cta: formatMessage(fallbacks.postOrderCardCta),
      foregroundColor: theme.colors.WHITE,
      deepLink: '',
      image: '',
      imageA11y: '',
    };

    const fallbackPostOrderLoyalty: Required<HomeBannerContent> = {
      id: 'fallback-post-order-loyalty',
      title: formatMessage(fallbacks.postOrderLoyaltyTitle),
      description: formatMessage(fallbacks.postOrderLoyaltyDescription),
      cta: formatMessage(fallbacks.postOrderLoyaltyCta),
      foregroundColor: theme.colors.WHITE,
      deepLink: '',
      image: '',
      imageA11y: '',
    };

    const postOrderBannerXs = mapBannerContentWithFallback(content?.postOrderBannerXs, fallbackPostOrderBanner); // prettier-ignore
    const postOrderBannerSm = mapBannerContentWithFallback(content?.postOrderBannerSm, fallbackPostOrderBanner); // prettier-ignore
    const postOrderCard = mapBannerContentWithFallback(content?.postOrderCard, fallbackPostOrderCard); // prettier-ignore
    const postOrderCardMultiple = mapBannerContentWithFallback(content?.postOrderCardMultiple, fallbackPostOrderCardMultiple); // prettier-ignore
    const postOrderLoyalty = mapBannerContentWithFallback(content?.postOrderLoyalty, fallbackPostOrderLoyalty); // prettier-ignore

    const footerTitle = content?.footerTitle ?? formatMessage(fallbacks.footerTitle);
    const footerImageUrl = content?.footerImage?.fields?.file?.url;
    const footerImageA11y = content?.footerImage?.fields?.description ?? formatMessage(fallbacks.footerImageA11y); // prettier-ignore

    return {
      filtersTitle,
      filterableCategories,
      genericCategories,
      fallBackBanner,
      homeBannerSignedOutXs,
      homeBannerSignedOutLg,
      homeBannerSignedInXs,
      homeBannerSignedInLg,
      postOrderBannerXs,
      postOrderBannerSm,
      postOrderCard,
      postOrderCardMultiple,
      postOrderLoyalty,
      footerTitle,
      footerImageUrl,
      footerImageA11y,
    };
  }, [content, formatMessage]);
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

export function getBannerContent(
  content: ReturnType<typeof useHomeContent>,
  isLoggedIn: boolean,
  isXS: boolean,
) {
  const fallBackBanner = content.fallBackBanner;

  const signedInBanners = isXS ? content.homeBannerSignedInXs : content.homeBannerSignedInLg;
  const signedOutBanners = isXS ? content.homeBannerSignedOutXs : content.homeBannerSignedOutLg;
  const banners = isLoggedIn ? signedInBanners : signedOutBanners;

  return banners.length > 0 ? banners : [fallBackBanner];
}

function mapBannerContent(banner: FlexibleContent): HomeBannerContent {
  return {
    id: banner?.fields?.id ?? '',
    title: banner?.fields?.title,
    description: banner?.fields?.description,
    cta: banner?.fields?.cta,
    foregroundColor: banner?.fields?.foregroundColor ?? theme.colors.WHITE,
    deepLink: banner?.fields?.deepLink,
    image: banner?.fields?.image?.fields?.file?.url ?? '',
    imageA11y: banner?.fields?.image?.fields?.description ?? '',
  };
}

function mapBannerContentWithFallback(
  banner: FlexibleContent | undefined,
  fallback: Required<HomeBannerContent>,
): Required<HomeBannerContent> {
  return {
    id: banner?.fields?.id ?? fallback.id,
    title: banner?.fields?.title ?? fallback.title,
    description: banner?.fields?.description ?? fallback.description,
    cta: banner?.fields?.cta ?? fallback.cta,
    foregroundColor: banner?.fields?.foregroundColor ?? fallback.foregroundColor,
    deepLink: banner?.fields?.deepLink ?? fallback.deepLink,
    image: banner?.fields?.image?.fields?.file?.url ?? fallback.image,
    imageA11y: banner?.fields?.image?.fields?.description ?? fallback.imageA11y,
  };
}

function getCategories(categories: HomeContent['genericCategories']) {
  const rawCategories = categories?.flatMap((category) =>
    category?.fields ? [category.fields] : [],
  );

  return rawCategories?.map(getCategoryWithProducts) ?? [];
}

function getCategoryWithProducts(category: ProductCategory['fields']) {
  const rawProducts = category?.products?.flatMap((product) => {
    const fields = product?.fields;
    if (!fields) return [];

    return [
      {
        lineageKey: fields.id,
        title: fields.title,
        description: fields.description,
        deepLink: fields.deepLink,
        contentfulImage: fields.image?.fields?.file?.url,
        imageA11y: fields.image?.fields?.description,

        // to be provided by the menu contentful override.
        calorieOverrideText: undefined as string | undefined,

        // to be provided by the menu query.
        isEnriched: false,
        id: '',
        slug: '',
        baseProductSlug: '',
        cloudinaryImage: '',
        isModifiable: false,
        cost: 0,
        calories: 0,
        totalFatG: 0,
        totalCarbsG: 0,
        dietaryFiberG: 0,
        sugarsG: 0,
        proteinG: 0,
        co2eKg: 0,
        allergenProperties: [] as readonly AllergenProperty[],
      },
    ];
  });

  return {
    id: category.id,
    categoryName: category.categoryName,
    products: rawProducts,
    showCalories: category.showCalories,
    showFat: category.showFat,
    showCarbs: category.showCarbs,
    showFiber: category.showFiber,
    showSugars: category.showSugars,
    showProtein: category.showProtein,
    showCo2: category.showCo2,
  };
}

// ─── Fallbacks ───────────────────────────────────────────────────────────────

function renderBreak() {
  return '\n';
}

const evergreenLink = 'https://www.sweetgreen.com/mission';

const fallbacks = defineMessages({
  fallbackBannerTitle: {
    defaultMessage: 'Freshly made,<br></br> just for you',
    description: 'Home | Fallback | Banner Title',
  },
  fallbackBannerDescription: {
    defaultMessage:
      'Fresh ingredients, made to order, ready when you are. Your next great meal is a few taps away.',
    description: 'Home | Fallback | Banner Description',
  },
  fallbackBannerCta: {
    defaultMessage: 'Start order',
    description: 'Home | Fallback | Banner CTA',
  },
  fallbackImageA11y: {
    defaultMessage: 'People enjoying a salad at Sweetgreen.',
    description: 'Home | Fallback | Banner Image A11y',
  },
  filtersTitle: {
    defaultMessage: 'What can we help you find?',
    description: 'Dietary Preferences | Fallback | Title',
  },
  postOrderBannerTitle: {
    defaultMessage: 'Thanks for supporting Drake Family Farm.',
    description: 'Home | Post Order | Banner Title',
  },
  postOrderBannerDescription: {
    defaultMessage:
      '10+ years of partnership to bring you the creamiest, most flavorful goat cheese.',
    description: 'Home | Post Order | Banner Description',
  },
  postOrderBannerCta: {
    defaultMessage: 'Learn more',
    description: 'Home | Post Order | Banner CTA',
  },
  postOrderBannerImageA11y: {
    defaultMessage: 'Drake family and their goats.',
    description: 'Home | Post Order | Banner Image A11y',
  },
  postOrderCardTitle: {
    defaultMessage: 'Your order is on its way!',
    description: 'Home | Post Order | Card Title',
  },
  postOrderCardMultipleTitle: {
    defaultMessage: 'You have multiple orders in progress',
    description: 'Home | Post Order | Card Title',
  },
  postOrderCardDescription: {
    defaultMessage: 'Scratch made. Almost yours.',
    description: 'Home | Post Order | Card Description',
  },
  postOrderCardCta: {
    defaultMessage: 'View order',
    description: 'Home | Post Order | Card CTA',
  },
  postOrderLoyaltyTitle: {
    defaultMessage: 'Earn rewards for every order.',
    description: 'Home | Post Order | Loyalty Title',
  },
  postOrderLoyaltyDescription: {
    defaultMessage: 'Check your account in 24h.',
    description: 'Home | Post Order | Loyalty Description',
  },
  postOrderLoyaltyCta: {
    defaultMessage: 'Rewards',
    description: 'Home | Post Order | Loyalty CTA',
  },
  footerTitle: {
    defaultMessage: 'See full menu',
    description: 'Home | Footer | Title',
  },
  footerImageA11y: {
    defaultMessage: 'Sweetgreen menu.',
    description: 'Home | Footer | Image A11y',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type FlexibleContent = Entry<{
  id: string;
  title?: string;
  description?: string;
  cta?: string;
  foregroundColor?: string;
  deepLink?: string;
  image?: Asset;
}>;

type ProductCategory = Entry<{
  id: string;
  categoryName: string;
  products: FlexibleContent[];
  showCalories?: boolean;
  showFat?: boolean;
  showCarbs?: boolean;
  showFiber?: boolean;
  showSugars?: boolean;
  showProtein?: boolean;
  showCo2?: boolean;
}>;

export type HomeProductCategory = ReturnType<typeof getCategoryWithProducts>;

export type HomeContent = {
  filtersTitle?: string;
  filterableCategories?: ProductCategory[];
  genericCategories?: ProductCategory[];
  homeBannerSignedOutXs?: FlexibleContent[];
  homeBannerSignedOutLg?: FlexibleContent[];
  homeBannerSignedInXs?: FlexibleContent[];
  homeBannerSignedInLg?: FlexibleContent[];
  postOrderBannerXs?: FlexibleContent;
  postOrderBannerSm?: FlexibleContent;
  postOrderCard?: FlexibleContent;
  postOrderCardMultiple?: FlexibleContent;
  postOrderLoyalty?: FlexibleContent;
  footerTitle?: string;
  footerImage?: Asset;
};

export type HomeBannerContent = {
  id: string;
  title?: string;
  description?: string;
  cta?: string;
  foregroundColor?: string;
  deepLink?: string;
  image?: string;
  imageA11y?: string;
};
